import { useState, useEffect } from 'react';
import { Check, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

const TEMPLATES = [
  {
    id: 'tpl-01',
    name: 'Classic',
    description: 'Clean two-column layout with company logo at top',
    preview: [
      { w: '100%', h: 6, bg: 'var(--accent)', rounded: true },
      { w: '60%', h: 3, bg: 'var(--border)', rounded: true, mt: 4 },
      { w: '40%', h: 3, bg: 'var(--border)', rounded: true, mt: 2 },
      { w: '100%', h: 16, bg: 'var(--bg-muted)', rounded: true, mt: 4 },
      { w: '80%', h: 3, bg: 'var(--border)', rounded: true, mt: 3 },
      { w: '80%', h: 3, bg: 'var(--border)', rounded: true, mt: 2 },
    ],
  },
  {
    id: 'tpl-02',
    name: 'Modern',
    description: 'Accent sidebar with earnings/deductions split view',
    preview: [
      { w: '30%', h: 40, bg: 'var(--accent)', rounded: true, float: 'left' },
      { w: '65%', h: 4, bg: 'var(--border)', rounded: true, mt: 2, ml: '35%' },
      { w: '65%', h: 4, bg: 'var(--border)', rounded: true, mt: 2, ml: '35%' },
      { w: '65%', h: 4, bg: 'var(--border)', rounded: true, mt: 2, ml: '35%' },
    ],
  },
  {
    id: 'tpl-03',
    name: 'Minimal',
    description: 'Plain text format, printer-friendly, no color blocks',
    preview: [
      { w: '50%', h: 3, bg: 'var(--text-tertiary)', rounded: true, opacity: 0.4 },
      { w: '30%', h: 3, bg: 'var(--text-tertiary)', rounded: true, mt: 2, opacity: 0.3 },
      { w: '100%', h: 1, bg: 'var(--border)', rounded: false, mt: 4 },
      { w: '90%', h: 2, bg: 'var(--text-tertiary)', rounded: true, mt: 3, opacity: 0.3 },
      { w: '90%', h: 2, bg: 'var(--text-tertiary)', rounded: true, mt: 2, opacity: 0.3 },
      { w: '90%', h: 2, bg: 'var(--text-tertiary)', rounded: true, mt: 2, opacity: 0.2 },
      { w: '100%', h: 1, bg: 'var(--border)', rounded: false, mt: 3 },
      { w: '60%', h: 3, bg: 'var(--text-tertiary)', rounded: true, mt: 3, opacity: 0.4 },
    ],
  },
];

function TemplatePreview({ blocks }) {
  return (
    <div className="w-full h-28 p-3 bg-white dark:bg-[var(--bg-primary)] rounded-[10px] overflow-hidden relative">
      {blocks.map((b, i) => (
        <div key={i} style={{
          width: b.w, height: b.h,
          borderRadius: b.rounded ? 4 : 0, marginTop: b.mt || 0, opacity: b.opacity || 1,
          background: b.bg,
        }} className="block"/>
      ))}
    </div>
  );
}

export default function PayslipTemplatesSettingsPage() {
  const { data } = useCompanySettings();
  const { mutate, isPending } = useUpdateCompanySettings();
  const [selected, setSelected] = useState('tpl-01');

  useEffect(() => {
    const saved = data?.data?.settings?.payslip_template;
    if (saved) setSelected(saved);
  }, [data]);

  const handleSave = () => {
    mutate({ settings: { payslip_template: selected } }, {
      onSuccess: () => toast.success('Payslip template saved'),
      onError: () => toast.error('Failed to save'),
    });
  };

  return (
    <div className="space-y-5 fade-in">
      <PageHeader title="Payslip Templates" breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Payslip Templates' }]}/>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {TEMPLATES.map(t => (
          <button key={t.id} onClick={() => setSelected(t.id)}
            className={`text-left p-4 rounded-[16px] border-2 transition-all space-y-3 ${selected === t.id ? 'border-[var(--accent)] bg-[var(--accent)]/5' : 'border-[var(--border)] bg-[var(--bg-card)] hover:border-[var(--accent)]/50'}`}>
            <div className="relative">
              <TemplatePreview blocks={t.preview}/>
              {selected === t.id && (
                <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[var(--accent)] flex items-center justify-center">
                  <Check size={12} className="text-white"/>
                </div>
              )}
            </div>
            <div>
              <p className="text-[14px] font-bold text-[var(--text-primary)]">{t.name}</p>
              <p className="text-[12px] text-[var(--text-secondary)] mt-0.5">{t.description}</p>
            </div>
          </button>
        ))}
      </div>

      <Button icon={Save} onClick={handleSave} disabled={isPending}>{isPending ? 'Saving…' : 'Save Template'}</Button>
    </div>
  );
}
